import type { StoredNotification } from '@cabgo/shared';
import { apiGet, apiPost } from './api';

export const SOUND_MUTED_KEY = 'cabgo.notifications.muted';

export async function fetchNotifications(): Promise<StoredNotification[]> {
  const res = await apiGet<{ data: StoredNotification[] }>('/notifications');
  return res.data ?? [];
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiPost(`/notifications/${encodeURIComponent(id)}/read`, {});
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiPost('/notifications/read-all', {});
}

export function isSoundMuted(): boolean {
  try {
    return localStorage.getItem(SOUND_MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted: boolean): void {
  try {
    localStorage.setItem(SOUND_MUTED_KEY, muted ? 'true' : 'false');
  } catch {}
}

/**
 * Play a gentle 2-tone melodic chime (E5 -> A5) via Web Audio API.
 * Synthesized in memory — requires no external asset files and degrades
 * silently if unprompted audio autoplay is prevented by the browser.
 */
export function playNotificationChime(): void {
  try {
    if (isSoundMuted()) return;
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const now = ctx.currentTime;

    // Tone 1: E5 (659.25 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now);
    gain1.gain.setValueAtTime(0.12, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Tone 2: A5 (880.00 Hz) with slight delay
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, now + 0.1);
    gain2.gain.setValueAtTime(0.16, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.55);
  } catch {
    // Autoplay restrictions or audio device issues fail gracefully.
  }
}
